import { GetProjectTokensQueryVariables, ProjectDetailFragment } from '@/gql/generated/graphql'
import { AttributeField, AttributesFilter } from '@utils/token-features'
import { parseAsBoolean, useQueryState, useQueryStates } from 'nuqs'
import { ChangeEventHandler, useState } from 'react'
import { isWalletAddress, isEnsAddress } from '@utils/artists'
import { useEnsAddress } from 'wagmi'
import { useSession } from 'next-auth/react'
import { ValidSorting, constructGetTokensQueryVariables, tokenFilterParsers } from '../utils'
import { useAuth } from '@/app/auth/artblocks-auth-provider'

export type UseTokenFiltersReturn = {
  featureFields: AttributeField[] | undefined
  featureFieldsCounts?: Record<string, Record<string, number>>
  tokenFilter: AttributesFilter
  setTokenFilter: (newAttributesFilter: AttributesFilter | null) => void
  sortOrder: ValidSorting
  sortOptions: ValidSorting[]
  setSortOrder: (newSortOrder: ValidSorting) => void
  // Final query variables
  tokenQueryVariables: GetProjectTokensQueryVariables
  // Page number
  setPageNumber: (pageNumber: number) => void
  pageNumber: number
  // Search by invoocation
  searchInvocationValue: string
  searchInvocationSavedValues: number[]
  handleSearchInvocationChange: React.ChangeEventHandler<HTMLInputElement>
  handleSaveSearchInvocation: () => void
  handleRemoveSearchInvocation: (value: number) => void
  clearSearchInvocationValue: () => void
  // Filter by marketplace
  abListingsOnly: boolean
  setABListingsOnly: (abListingsOnly: boolean | 'indeterminate') => void
  // Filter by wallet addresses
  walletAddressValue: string
  walletAddressSavedValues: string[]
  handleWalletAddressChange: React.ChangeEventHandler<HTMLInputElement>
  handleSaveWalletAddress: () => void
  handleRemoveWalletAddress: (value: string) => void
  clearWalletAddressValue: () => void
  ensAddresses: Record<string, string>
  myArtworksOnly: boolean
  handleMyArtworksOnlyChange: (value: boolean | 'indeterminate') => void
}

export function useTokenFilters(projectMetadata?: ProjectDetailFragment): UseTokenFiltersReturn {
  const { data: session, status } = useSession()
  const { login } = useAuth()
  const [{ attributes: tokenFilter, page: pageNumber, sort: sortOrder }, setQueryStates] =
    useQueryStates(tokenFilterParsers)

  const setTokenFilter = (newAttributesFilter: AttributesFilter | null) => {
    setQueryStates({ attributes: newAttributesFilter, page: null })
  }

  const setPageNumber = (pageNumber: number) => {
    setQueryStates({ page: pageNumber })
  }

  const setSortOrder = (newSortOrder: ValidSorting) => {
    setQueryStates({ sort: newSortOrder })
  }

  // Project feature fields formatted for AttributesFilterControl component
  const featureFields: AttributeField[] | undefined = projectMetadata?.features?.feature_fields
  const featureFieldsCounts = projectMetadata?.features?.feature_fields_counts

  const [searchInvocationValue, setSearchInvocationValue] = useState<string>('')
  const [searchInvocationSavedValues, setSearchInvocationSavedValues] = useState<number[]>([])

  const clearSearchInvocationValue = () => {
    setSearchInvocationValue('')
    setSearchInvocationSavedValues([])
    setQueryStates({ page: null })
  }

  const handleSearchInvocationChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearchInvocationValue(event.target.value)
  }

  const handleSaveSearchInvocation = () => {
    const newValue = parseInt(searchInvocationValue)
    if (
      !searchInvocationSavedValues.includes(newValue) &&
      (!projectMetadata?.max_invocations ||
        (projectMetadata?.max_invocations && newValue < projectMetadata?.max_invocations))
    ) {
      setSearchInvocationSavedValues([...searchInvocationSavedValues, newValue])
    }

    setQueryStates({ page: null })
    setSearchInvocationValue('')
  }

  const handleRemoveSearchInvocation = (value: number) => {
    setSearchInvocationSavedValues(searchInvocationSavedValues.filter((v) => v !== value))
  }

  const [abListingsOnly, setAbListingsOnlyNuqs] = useQueryState('abListingsOnly', parseAsBoolean)
  const setABListingsOnly = (abListingsOnly: boolean | 'indeterminate') => {
    setAbListingsOnlyNuqs(abListingsOnly === 'indeterminate' ? false : abListingsOnly)
  }

  // Wallet address filtering
  const [walletAddressValue, setWalletAddressValue] = useState<string>('')
  const [walletAddressSavedValues, setWalletAddressSavedValues] = useState<string[]>([])
  const [ensAddresses, setEnsAddresses] = useState<Record<string, string>>({})

  const { data: resolvedEnsAddress } = useEnsAddress({
    name: isEnsAddress(walletAddressValue) ? walletAddressValue : undefined,
    query: {
      enabled: isEnsAddress(walletAddressValue)
    }
  })

  const clearWalletAddressValue = () => {
    setWalletAddressValue('')
    setWalletAddressSavedValues([])
    setEnsAddresses({})
    setQueryStates({ page: null })
  }

  const handleWalletAddressChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    setWalletAddressValue(event.target.value)
  }

  const handleSaveWalletAddress = () => {
    let newValue = walletAddressValue.trim()

    if (isEnsAddress(newValue)) {
      if (resolvedEnsAddress) {
        newValue = resolvedEnsAddress.toLowerCase()
        setEnsAddresses((prev) => ({ ...prev, [newValue]: walletAddressValue }))
      } else {
        return // Don't save if ENS is not resolved
      }
    } else if (isWalletAddress(newValue)) {
      newValue = newValue.toLowerCase()
    } else {
      return // Don't save if not a valid address or ENS
    }

    if (newValue && !walletAddressSavedValues.includes(newValue)) {
      setWalletAddressSavedValues([...walletAddressSavedValues, newValue])
    }

    setQueryStates({ page: null })
    setWalletAddressValue('')
  }

  const handleRemoveWalletAddress = (value: string) => {
    setWalletAddressSavedValues(walletAddressSavedValues.filter((v) => v !== value))
    setEnsAddresses((prev) => {
      const newEnsAddresses = { ...prev }
      delete newEnsAddresses[value]
      return newEnsAddresses
    })
  }

  const [myArtworksOnly, setMyArtworksOnly] = useState(false)

  const handleMyArtworksOnlyChange = (value: boolean | 'indeterminate') => {
    if (status === 'unauthenticated') {
      login?.()
      return
    }
    setMyArtworksOnly(value === true)
  }

  const tokenQueryVariables: GetProjectTokensQueryVariables = constructGetTokensQueryVariables({
    projectId: projectMetadata?.id,
    tokenFilter: tokenFilter ?? {},
    pageNumber,
    sortOrder,
    searchInvocationValues: searchInvocationSavedValues,
    abListingsOnly,
    walletAddresses: myArtworksOnly && session?.address ? [session.address] : walletAddressSavedValues
  })

  return {
    // AttributesFilterControl props
    featureFields,
    featureFieldsCounts,
    // Filters
    tokenFilter: tokenFilter ?? {},
    setTokenFilter,
    // Order
    sortOrder: sortOrder,
    sortOptions: Object.values(ValidSorting),
    setSortOrder,
    // Final query variables
    tokenQueryVariables,
    // Page number
    pageNumber: pageNumber,
    setPageNumber,
    //search by invoocation
    searchInvocationValue,
    searchInvocationSavedValues,
    handleSearchInvocationChange,
    clearSearchInvocationValue,
    handleSaveSearchInvocation,
    handleRemoveSearchInvocation,
    // Filter by marketplace
    abListingsOnly: abListingsOnly ?? false,
    setABListingsOnly,
    // Filter by wallet addresses
    walletAddressValue,
    walletAddressSavedValues,
    handleWalletAddressChange,
    clearWalletAddressValue,
    handleSaveWalletAddress,
    handleRemoveWalletAddress,
    ensAddresses,
    myArtworksOnly,
    handleMyArtworksOnlyChange
  }
}
